import {
  Graphics,
  Container,
  ContainerChild,
  Text,
  TextStyle,
  Sprite,
  Texture,
} from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetTimer,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { TimerService } from "../Services/TimerService";
import { ensureFontReady } from "../../../../hooks/useGoogleFont";
import {
  anchorXForAlign,
  computeLetterSpacingPx,
  computeLineHeightPx,
  decorationThickness,
  underlineOffsetPx,
} from "../../../Text/textLayout";
import {
  getTimerDesign,
  renderTimerDesignSvg,
  type TimerDesign,
} from "../../../../data/timerDesignsCatalog";
import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "./svgTextureRaster";

/**
 * Per-design-card raster state for one timer widget. Lets the tick callback
 * re-rasterise ONLY when the displayed MM:SS string changes (≈1/sec) instead
 * of every animation frame, and lets `destroy()` reclaim the GPU texture.
 */
interface IDesignRenderState {
  sprite: Sprite;
  design: TimerDesign;
  renderScale: number;
  /** Widget bounds the sprite is stretched to — re-asserted after each swap. */
  destWidth: number;
  destHeight: number;
  /** Last MM:SS string baked into the current texture. */
  lastTime: string | null;
  /** Texture currently shown — destroyed before being replaced. */
  texture: Texture | null;
  /** Bumped per re-raster so a slow async decode can't apply out of order. */
  generation: number;
  destroyed: boolean;
}

export class TimerWidgetRenderer {
  private textObjects = new Map<string, Text>();

  private updateCallbacks = new Map<string, (time: number) => void>();

  private startTimes = new Map<string, number>();

  private designStates = new Map<string, IDesignRenderState>();

  render(
    widget: IStudioWidget,

    container: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetTimer).options;

    const backgroundColor = options.backgroundColor;

    if (backgroundColor && backgroundColor !== "transparent") {
      const bg = new Graphics();

      bg.rect(0, 0, destination.width, destination.height).fill(
        backgroundColor,
      );

      container.addChild(bg);
    }

    const fontSize = options.fontSize ? parseInt(options.fontSize) : 24;
    const fontFamily = options.fontFamily || "Arial";
    const color = options.color || "#FFFFFF";
    const bold = options.bold !== false; // historical default: bold
    const italic = Boolean(options.italic);
    const align = options.textAlign ?? "center";
    const lineHeightPx = computeLineHeightPx(fontSize, options.lineHeight ?? 1);
    const letterSpacingPx = computeLetterSpacingPx(
      options.wordSpacing,
      fontSize,
    );

    const timerService = TimerService.getInstance();

    // Use a persistent start time for this specific widget to ensure it starts at 0 and persists across re-renders (e.g. drags)

    const widgetId = widget.id;

    const timerKey = `timer_${widgetId}`;

    let startTime = this.startTimes.get(timerKey);

    if (!startTime) {
      startTime = timerService.getStartTime();

      this.startTimes.set(timerKey, startTime);
    }

    // ── Design-store path ────────────────────────────────────────────────
    // When the widget has a resolvable `options.designId`, render the chosen
    // design's CARD SVG (from the local catalog) as a Pixi texture at the
    // widget bounds, re-rasterising only when the MM:SS string changes. If the
    // id is absent/unknown we fall through to the legacy text-timer render
    // below, completely untouched.
    //
    // `designId` is an optional, backward-compat field added to the timer
    // options contract by a sibling task; read it defensively so this renderer
    // compiles regardless of whether that field has landed yet.
    const designId = (options as { designId?: string }).designId;
    const design = getTimerDesign(designId);

    if (design) {
      this.renderDesign(
        timerKey,
        design,
        startTime,
        options,
        container,
        destination,
        timerService,
      );
      return;
    }

    const buildStyle = () =>
      new TextStyle({
        fontFamily,
        fontSize,
        fill: color,
        align,
        fontWeight: bold ? "bold" : "normal",
        fontStyle: italic ? "italic" : "normal",
        lineHeight: lineHeightPx,
        letterSpacing: letterSpacingPx,
      });

    const text = new Text({
      text: this.calculateTimer(startTime),
      style: buildStyle(),
    });

    // Bust Pixi's text-sprite cache once the Google Font face is decoded
    // — see SimpleRenderer for the full explanation.
    void ensureFontReady(fontFamily, fontSize, bold ? 700 : 400).then(() => {
      text.style = buildStyle();
    });

    text.anchor.set(anchorXForAlign(align), 0.5);
    text.x =
      align === "left"
        ? 0
        : align === "right"
          ? destination.width
          : destination.width / 2;
    text.y = destination.height / 2;

    container.addChild(text as unknown as ContainerChild);

    // Underline / strike — drawn as Graphics centered on each line slot,
    // mirroring `TextElement.ts`. The Timer is single-line so this is
    // a one-rect path; kept generic in case lineHeight ever pushes the
    // bbox above one line.
    if (options.underline || options.strike) {
      const thickness = decorationThickness(fontSize);
      const uOffset = underlineOffsetPx(fontSize);
      const graphics = new Graphics();
      const lineWidth = text.width;
      const yMid = 0;
      const lineStartX =
        align === "center"
          ? -lineWidth / 2
          : align === "right"
            ? -lineWidth
            : 0;
      if (options.underline) {
        graphics
          .rect(
            lineStartX,
            yMid + uOffset - thickness / 2,
            lineWidth,
            thickness,
          )
          .fill({
            color,
          });
      }
      if (options.strike) {
        graphics
          .rect(lineStartX, yMid - thickness / 2, lineWidth, thickness)
          .fill({
            color,
          });
      }
      graphics.x = text.x;
      graphics.y = text.y;
      container.addChild(graphics);
    }

    this.textObjects.set(timerKey, text);

    // Clean up previous callback if exists (though usually destroy handles it)

    if (this.updateCallbacks.has(timerKey)) {
      const oldCallback = this.updateCallbacks.get(timerKey)!;

      timerService.unsubscribe(oldCallback);
    }

    const updateCallback = (now: number) => {
      const timeString = this.calculateTimer(startTime!, now, options);

      if (text.text !== timeString) {
        text.text = timeString;
      }
    };

    this.updateCallbacks.set(timerKey, updateCallback);

    timerService.subscribe(updateCallback);
  }

  /**
   * Render the selected design's card SVG as a Pixi sprite at the widget
   * bounds, then keep it live by re-rasterising the texture whenever the
   * displayed MM:SS string changes (driven by the same 1 s TimerService tick
   * the text path uses). The sprite is stretched to fill `destination` exactly
   * — the SVG's own viewBox preserves the card's internal proportions, and the
   * card aspect (≈2:1) matching the widget bounds is the streamer's job (the
   * store seeds sensible bounds), mirroring how QR/overlay images fill.
   */
  private renderDesign(
    timerKey: string,
    design: TimerDesign,
    startTime: number,
    options: IStudioWidgetTimer["options"],
    container: Container,
    destination: { x: number; y: number; width: number; height: number },
    timerService: TimerService,
  ): void {
    // Optional solid backdrop, same semantics as the text path.
    const backgroundColor = options.backgroundColor;
    if (backgroundColor && backgroundColor !== "transparent") {
      const bg = new Graphics();
      bg.rect(0, 0, destination.width, destination.height).fill(
        backgroundColor,
      );
      container.addChild(bg);
    }

    // Backing-store resolution: rasterise at the larger of the card's native
    // size and the on-canvas size, capped so an oversized widget can't ask for
    // a multi-thousand-px bitmap. The sprite is then scaled to the bounds.
    const scaleForBounds = Math.max(
      1,
      destination.width / design.cardWidth,
      destination.height / design.cardHeight,
    );
    const renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = Math.max(1, destination.width);
    sprite.height = Math.max(1, destination.height);
    sprite.position.set(0, 0);
    container.addChild(sprite as unknown as ContainerChild);

    const state: IDesignRenderState = {
      sprite,
      design,
      renderScale,
      destWidth: Math.max(1, destination.width),
      destHeight: Math.max(1, destination.height),
      lastTime: null,
      texture: null,
      generation: 0,
      destroyed: false,
    };

    // Replace any previous state for this widget (re-render on drag/edit). The
    // old sprite is destroyed with its container by WidgetsFactory, but its
    // off-screen texture is ours to free — do it now so repeated re-renders
    // don't leak GPU memory. Also unsubscribe its tick callback below.
    const prev = this.designStates.get(timerKey);
    if (prev) {
      prev.destroyed = true;
      if (prev.texture && prev.texture !== Texture.EMPTY) {
        try {
          prev.texture.destroy(true);
        } catch {
          /* best-effort */
        }
        prev.texture = null;
      }
    }
    this.designStates.set(timerKey, state);

    // Load the design's Google fonts up front, then draw the first frame.
    // We also draw immediately with whatever is already decoded so the slot is
    // never empty; the post-font draw upgrades the typeface once it's ready.
    const firstTime = this.calculateTimer(startTime, Date.now(), options);
    void this.rerasterizeDesign(timerKey, firstTime);
    void ensureDesignFontsReady(design.fonts).then(() => {
      if (state.destroyed) return;
      // Redraw with the CURRENT time now that the real fonts are decoded (the
      // first draw may have used a system fallback, and the clock may have
      // advanced while the face was loading). Force it past the equality guard.
      state.lastTime = null;
      void this.rerasterizeDesign(
        timerKey,
        this.calculateTimer(startTime, Date.now(), options),
      );
    });

    // Unsubscribe a stale callback for this widget (defensive — destroy()
    // normally handles teardown).
    if (this.updateCallbacks.has(timerKey)) {
      timerService.unsubscribe(this.updateCallbacks.get(timerKey)!);
    }

    const updateCallback = (now: number) => {
      if (state.destroyed) return;
      const timeString = this.calculateTimer(startTime, now, options);
      // Re-raster ONLY when the MM:SS string actually changed — not every tick
      // and never every frame.
      if (timeString !== state.lastTime) {
        void this.rerasterizeDesign(timerKey, timeString);
      }
    };

    this.updateCallbacks.set(timerKey, updateCallback);
    timerService.subscribe(updateCallback);
  }

  /**
   * (Re)build the SVG → texture for a design widget at the given time string
   * and swap it onto the sprite. Generation-guarded so a slow decode that
   * resolves after a newer one can't clobber the fresher frame, and after
   * teardown.
   */
  private async rerasterizeDesign(
    timerKey: string,
    timeString: string,
  ): Promise<void> {
    const state = this.designStates.get(timerKey);
    if (!state || state.destroyed) return;

    // Claim this generation immediately so the equality guard in the tick
    // callback doesn't fire a second raster for the same string mid-decode.
    state.lastTime = timeString;
    const gen = ++state.generation;

    const svg = renderTimerDesignSvg(state.design, timeString);

    try {
      const texture = await rasterizeSvgToTexture(
        svg,
        state.design.cardWidth,
        state.design.cardHeight,
        state.renderScale,
      );

      // A newer raster (or teardown) won the race — drop this one.
      if (
        state.destroyed ||
        gen !== state.generation ||
        state.sprite.destroyed
      ) {
        texture.destroy(true);
        return;
      }

      const old = state.texture;
      state.texture = texture;
      state.sprite.texture = texture;
      // Assigning a texture in Pixi v8 resets the sprite to the texture's
      // intrinsic (backing-store) size — re-assert the widget bounds so the
      // card always fills `destination` regardless of renderScale.
      state.sprite.width = state.destWidth;
      state.sprite.height = state.destHeight;
      if (old && old !== Texture.EMPTY) old.destroy(true);
    } catch {
      // Decode failed (malformed SVG / browser quirk) — keep the previous
      // frame on screen rather than blanking. Allow a future tick to retry by
      // clearing the cached string.
      if (!state.destroyed && gen === state.generation) {
        state.lastTime = null;
      }
    }
  }

  private calculateTimer(
    startTime: number,

    now: number = Date.now(),

    options?: IStudioWidgetTimer["options"],
  ): string {
    const elapsed = Math.max(0, now - startTime);

    const totalSeconds = Math.floor(elapsed / 1000);

    const days = Math.floor(totalSeconds / (60 * 60 * 24));

    const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));

    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);

    const seconds = totalSeconds % 60;

    const usePad = !options || options.padWithZeros !== false;

    const fmt = (num: number) =>
      usePad ? String(num).padStart(2, "0") : String(num);

    const parts: string[] = [];

    if (options?.showDays) parts.push(fmt(days));

    if (!options || options.showHours !== false) parts.push(fmt(hours));

    if (!options || options.showMinutes !== false) parts.push(fmt(minutes));

    if (!options || options.showSeconds !== false) parts.push(fmt(seconds));

    return parts.length > 0 ? parts.join(":") : "00:00:00";
  }

  public destroy() {
    const timerService = TimerService.getInstance();

    for (const [_key, callback] of this.updateCallbacks.entries()) {
      timerService.unsubscribe(callback);
    }

    this.updateCallbacks.clear();

    this.textObjects.clear();

    this.startTimes.clear();

    // Release design-card render state + GPU textures. The widget containers
    // (and their sprites) are destroyed by WidgetsFactory; we only own the
    // textures we created off-screen.
    for (const state of this.designStates.values()) {
      state.destroyed = true;
      if (state.texture && state.texture !== Texture.EMPTY) {
        try {
          state.texture.destroy(true);
        } catch {
          /* best-effort */
        }
      }
      state.texture = null;
    }

    this.designStates.clear();
  }
}
